import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import PropertyCard from "@/components/property/PropertyCard";
import SafeHtml from "@/components/common/SafeHtml";
import BrandedLoader from "@/components/common/BrandedLoader";

export default function Location() {
  const { slug } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/locations/${slug}`).then(({ data }) => setData(data));
  }, [slug]);

  if (!data) return <BrandedLoader />;

  return (
    <div data-testid="location-page">
      <Helmet>
        <title>{`Stays in ${data.name} · Vyagram Experiences`}</title>
        <meta name="description" content={data.description} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`Stays in ${data.name} · Vyagram Experiences`} />
        <meta property="og:description" content={data.description} />
        {data.image && <meta property="og:image" content={data.image} />}
        {data.image && <meta property="og:image:secure_url" content={data.image} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Stays in ${data.name}`} />
        <meta name="twitter:description" content={data.description} />
        {data.image && <meta name="twitter:image" content={data.image} />}
      </Helmet>
      <section className="relative isolate overflow-hidden">
        <img src={data.image} alt={data.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-forest/80 to-canopy/50" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-32 lg:px-10 lg:py-40">
          <p className="font-accent text-lg text-gold uppercase tracking-[0.2em]">Location</p>
          <h1 className="mt-3 font-display text-4xl text-parchment sm:text-6xl">{data.name}</h1>
          <p className="mt-3 text-xl text-gold/90 italic">{data.tagline}</p>
          <p className="editorial-body mt-5 max-w-2xl !text-parchment/85">{data.description}</p>
        </div>
      </section>

      {/* About this location — admin-editable HTML shown above the property grid */}
      {data.about_html && (
        <section className="mx-auto max-w-4xl px-4 pt-14 sm:px-6 lg:px-10" data-testid="location-about">
          <p className="vyg-eyebrow">About the neighbourhood</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl">A guided tour of {data.name}</h2>
          <SafeHtml html={data.about_html} className="editorial-body mt-5 prose-editorial" testId="location-about-html" />
        </section>
      )}

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-10">
        <div className="mt-2 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {data.properties?.map((p) => (<PropertyCard key={p.slug} p={p} />))}
        </div>
      </section>
    </div>
  );
}
